import { defineDocument } from '../../schema';
export const featureUserPreferencesDocument = defineDocument({
    id: 'feature:user-preferences',
    kind: 'feature',
    slug: 'user-preferences',
    route: '/discover/features/user-preferences',
    seo: {
        title: 'Discover User Preferences',
        description: 'Customize Poppy with 48 unique combinations of accent, gender, and personality. Set content style to match your group.',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Discover Feature',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'User Preferences',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Customize Poppy with 48 unique combinations of accent, gender, and personality. Set content style to match your group.',
            },
        ],
    },
    aside: {
        showToc: true,
    },
    sections: [
        {
            id: 'overview',
            title: [
                {
                    type: 'text',
                    text: 'Customizing Your Experience',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'User Preferences let you personalize and control the game experience for everyone in your game room. Set up and update your preferences anytime from the ',
                        },
                        {
                            type: 'link',
                            href: '/library',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Library',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' page.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Your current preferences are applied when you create a new room. Once a room is created, the settings for that room are locked and cannot be changed. If you want to apply a different set of preferences, update them in the Library and then create a new room.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'While we strive to align the game experience with your preferences, we cannot fully control the AI output. Generated content may on rare occasion be inappropriate or unexpected. Read more about our approach in ',
                        },
                        {
                            type: 'link',
                            href: '/terms#ai-generated-content',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Terms & Conditions',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'host-style',
            title: [
                {
                    type: 'text',
                    text: 'Host Style',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Host Style determines how Poppy sounds and interacts with your group. You can mix and match accent, gender, and personality to create 48 unique combinations. Open ',
                        },
                        {
                            type: 'link',
                            href: '/library/poppy',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Host Style',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' from the Library sidebar. Changes to Host Style auto-save and will apply the next time you create a room.',
                        },
                    ],
                },
                {
                    type: 'image',
                    imageAsset: 'featureHostStyle3',
                    imageAlt: 'Host Style customization interface showing accent, gender, and personality options',
                    caption: [
                        {
                            type: 'text',
                            text: 'Mix and match 48 combinations to find your perfect Poppy',
                        },
                    ],
                    maxWidth: 400,
                },
            ],
            sections: [
                {
                    id: 'host-style__accent',
                    title: [
                        {
                            type: 'text',
                            text: 'Accent',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Poppy can currently speak in four accents (American, Australian, British, Indian). More accents may be added as language support expands. In addition to the game audio, the accent you select influences the spelling and phrasing conventions used for generated game content.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'host-style__gender',
                    title: [
                        {
                            type: 'text',
                            text: 'Gender',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Select Male or Female to change the voice tone of Poppy.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'host-style__personality',
                    title: [
                        {
                            type: 'text',
                            text: 'Personality',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Pick the hosting style that fits your group:',
                                },
                            ],
                        },
                        {
                            type: 'list',
                            items: [
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Cheerleader',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Sweet and supportive. Poppy encourages everyone with warmth and positivity.',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Comedian',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Snarky and witty. Poppy brings sharp humor and playful roasting.',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Diva',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Dramatic and intense. Poppy delivers high energy and theatrical flair.',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Entertainer',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Quirky and whimsical. Poppy keeps things fun with an offbeat charm.',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Slacker',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Bored and unbothered. Poppy coasts through the game with dry, deadpan energy.',
                                    },
                                ],
                                [
                                    {
                                        type: 'strong',
                                        children: [
                                            {
                                                type: 'text',
                                                text: 'Villain',
                                            },
                                        ],
                                    },
                                    {
                                        type: 'text',
                                        text: ' - Menacing and mischievous. Poppy schemes up trouble with sinister intent.',
                                    },
                                ],
                            ],
                        },
                    ],
                },
                {
                    id: 'host-style__preview-voice',
                    title: [
                        {
                            type: 'text',
                            text: 'Preview Voice',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "After choosing a combination, press the play button in Poppy's mouth to hear a preview with your selected accent, gender, and personality. This helps you find the right combination before creating your next room.",
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'content-style',
            title: [
                {
                    type: 'text',
                    text: 'Content Style',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Content Style controls the spiciness of the content Poppy generates during gameplay. This setting helps hosts create the environment they want for their audience. Open ',
                        },
                        {
                            type: 'link',
                            href: '/library?section=content-style',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Content Style',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' from the Library. Content Style auto-saves when you update it and applies to the next room you create.',
                        },
                    ],
                },
                {
                    type: 'list',
                    items: [
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Standard',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' - Suitable for general audiences. This is the default option and works well for most groups. Content is appropriate for most audiences without being overly sanitized.',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Family-Friendly',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' - More approachable content with the intent of being safe and understandable for all audiences. Choose this when playing with groups or in environments where content safety is a priority.',
                            },
                        ],
                        [
                            {
                                type: 'strong',
                                children: [
                                    {
                                        type: 'text',
                                        text: 'Edgy',
                                    },
                                ],
                            },
                            {
                                type: 'text',
                                text: ' - Bold, provocative content for adult audiences. This style addresses societal taboos and intense themes. Only use Edgy when all players are adults and comfortable with mature humor and topics.',
                            },
                        ],
                    ],
                },
                {
                    type: 'callout',
                    tone: 'warning',
                    content: [
                        {
                            type: 'strong',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Important note: ',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: 'Content Style only affects Poppy-generated content and does not control player-generated content. To control player inputs, check out the ',
                        },
                        {
                            type: 'link',
                            href: '/discover/features/moderator-tools#censorship',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Censorship',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' Moderator Tools available.',
                        },
                    ],
                },
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Content Style and Moderator Tools can also be mixed and matched to create different kinds of rooms. For example, you might use Family-Friendly Content Style while allowing edgier player inputs, or use Edgy Content Style with aggressive profanity filtering enabled in Moderator Tools. These combinations can be part of the fun, giving Poppy one set of boundaries while player content and moderation settings shape the rest of the experience.',
                        },
                    ],
                },
            ],
        },
        {
            id: 'language',
            title: [
                {
                    type: 'text',
                    text: 'Language',
                },
            ],
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: 'Airhead Games is currently available in English. Additional languages are planned but not yet available.',
                        },
                    ],
                },
            ],
        },
    ],
});
//# sourceMappingURL=user-preferences.js.map